import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
	graphql,
	type PreloadedQuery,
	useFragment,
	usePaginationFragment,
	usePreloadedQuery,
} from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import { AnyAri } from '@atlassian/ari/any-ari';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import type { IssueLinkedPRs } from '@atlassian/search-client/hydration/pr-types';
import type { PastMeetingOccurrencesMap } from '@atlassian/search-client/past-meeting-occurrences/types';

import { is3pProductKeyForAnalytics } from '../../../common/constants/is3p-product-key-for-analytics';
import { isProductKey, ProductKeys } from '../../../common/constants/product-keys';
import { products1P } from '../../../common/constants/products';
import { ResultsStates } from '../../../common/constants/results';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { type DebugResult } from '../../../common/ui/debug-overrides-panel';
import { DebugOverridesPanel } from '../../../common/ui/debug-overrides-panel/async';
import { getLinksFromText } from '../../../common/ui/link-attachments';
import {
	LINKED_RESULTS_DEFAULT_EXPAND_SIZE,
	LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND,
} from '../../../common/ui/linked-results';
import type { ResultShown } from '../../../common/utils/events/types';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { getIntegrationKey } from '../../../common/utils/get-integration-key';
import { getProductFromProviderId } from '../../../common/utils/get-product-from-provider-id';
import { shouldHydrateJiraGitHubPRs } from '../../../common/utils/should-hydrate-jira-git-hub-prs';
import { getTeamId } from '../../../common/utils/teams';
import { getResultConnectorSource } from '../../../common/utils/third-party-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import useDwellTime from '../../../controllers/dwell';
import { useFeatures } from '../../../controllers/store/bootstrap';
import {
	useSearchActions,
	useWasContentClearedBySpaceChange,
} from '../../../controllers/store/search';
import { filter as spaceFilter } from '../../../controllers/store/search/filters/space';

import type { searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData.graphql';
import type { searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsBody_connectionData.graphql';
import type { SearchResultsAsyncPaginationQuery } from './__generated__/SearchResultsAsyncPaginationQuery.graphql';
import type { searchResultsAsyncQuery } from './__generated__/searchResultsAsyncQuery.graphql';
import AutoCorrection from './auto-correction';
import { fetchAndUpdateJiraFields } from './fetch-and-update-jira-fields';
import { fetchAndUpdateJiraLinkedPRs } from './fetch-and-update-jira-linked-p-rs';
import { fetchAndUpdatePastMeetingOccurrences } from './fetch-and-update-past-meeting-occurrences';
import { fetchAndUpdateTeamNames } from './fetch-and-update-team-names';
import { getJiraIssueHydrationInputs } from './get-jira-issue-hydration-inputs';
import { SearchResultsListLiteRenderer } from './search-result-list-lite-renderer';
import { isSearchCompassComponent } from './search-result/search-result-renderer/search-compass-component';
import {
	isSearchJiraIssue,
	type JiraIssueStatusAndColorName,
} from './search-result/search-result-renderer/search-jira-issue';
import { SearchResultsListDefaultRenderer } from './search-results-list-default-renderer';
import type { SearchResultsProps } from './types';
import { useAfterQueryPerformanceTiming } from './use-after-query-performance-timing';
import { useFederatedEmailResultCount } from './use-federated-email-result-count';
import { useRenderResultsOptimisation } from './use-render-results-optimisation';
import { useSearchResultsAnalytics } from './use-search-results-analytics';
import { useSearchResultsVitalStats } from './use-search-results-vital-stats';

const PAGINATION_UNUSED_PAGE_SIZE = 30;

type SearchResultsSharedProps = Omit<SearchResultsProps, 'searchResultsAsyncQuery'>;

type SearchResultsAsyncInnerProps = {
	queryData: searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key;
	queryReference: PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

type SearchResultsBodyProps = {
	connectionData: searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key | null;
	isCachedResult?: boolean | null;
	isLoadingNext: boolean;
	hasPreviousRef: React.MutableRefObject<boolean>;
	cursor: string | null | undefined;
	fetchNextPage: (nextCursor: string) => void;
	queryReference: PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

/**
 * Three-tier construction:
 * - SearchResultsAsync = toplevel usePreloadedQuery() - handles preloading, variable inputs, query-plan
 * - SearchResultsAsyncInner = usePaginationFragment - defines the actual asyncSearch() query, handles pagination
 * - SearchResultsBody = defines the actual fields for the query / response
 *
 *   SearchResultsAsync
 *   └── SearchResultsAsyncInner
 *   	└── SearchResultsBody
 */
export const SearchResultsAsync = ({
	searchResultsAsyncQuery: searchResultsAsyncQueryRef,
	...sharedProps
}: SearchResultsProps): React.JSX.Element => {
	const queryData = usePreloadedQuery<searchResultsAsyncQuery>(
		graphql`
			query searchResultsAsyncQuery(
				$query: String!
				$cloudIdARIs: [String!]!
				$tenantId: ID!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$matchingLayerExperimentsMetadata: SearchMatchingLayerExperimentsMetadata
				$after: String
				$disableQueryReplacement: Boolean
				$experience: String!
				$enableHighlighting: Boolean
				$fanoutExperimentCohort: String
				$expectResult: Boolean
				$includePositions: Boolean = false
				$includeThirdPartyId: Boolean = false
				$includeNavBoostScore: Boolean = false
				$includeDeactivatedContributors: Boolean = false
				$includePassionfruitAsk: Boolean = false
				$includeRisk: Boolean = false
				$includeJiraLiteModeFields: Boolean = false
				$includeGithubCode: Boolean = false
				$includeReactionsAndCommentCount: Boolean = false
			) {
				...searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData
					@arguments(
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
						includeDeactivatedContributors: $includeDeactivatedContributors
						includePassionfruitAsk: $includePassionfruitAsk
						includeRisk: $includeRisk
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeGithubCode: $includeGithubCode
						includeReactionsAndCommentCount: $includeReactionsAndCommentCount
					)
			}
		`,
		searchResultsAsyncQueryRef,
	);

	const inner = (
		<SearchResultsAsyncInner
			queryData={queryData}
			queryReference={searchResultsAsyncQueryRef}
			sharedProps={sharedProps}
		/>
	);

	return inner;
};

const SearchResultsAsyncInner = ({
	queryData,
	queryReference,
	sharedProps,
}: SearchResultsAsyncInnerProps): React.JSX.Element => {
	const hasPreviousRef = useRef(false);

	const { data, loadNext, isLoadingNext } = usePaginationFragment<
		SearchResultsAsyncPaginationQuery,
		searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key
	>(
		graphql`
			fragment searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData on Query
			@refetchable(queryName: "SearchResultsAsyncPaginationQuery")
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includeRisk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeDeactivatedContributors: { type: "Boolean", defaultValue: false }
				includeGithubCode: { type: "Boolean", defaultValue: false }
				includeReactionsAndCommentCount: { type: "Boolean", defaultValue: false }
			) {
				search {
					search: asyncSearch(
						experience: $experience
						enableHighlighting: $enableHighlighting
						query: $query
						first: $limit
						after: $after
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						fanoutExperimentCohort: $fanoutExperimentCohort
						expectResult: $expectResult
						filters: {
							entities: $entities
							locations: $cloudIdARIs
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							assetsFilters: $assetsFilters
							thirdPartyFilters: $thirdPartyFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
						matchingLayerExperimentsMetadata: $matchingLayerExperimentsMetadata
					) @connection(key: "SearchResultsAsyncPagination__search") {
						isCachedResult
						...searchResults_AtlassianSearchPage_SearchResultsBody_connectionData
							@arguments(
								includeJiraLiteModeFields: $includeJiraLiteModeFields
								includePassionfruitAsk: $includePassionfruitAsk
								includePositions: $includePositions
								includeThirdPartyId: $includeThirdPartyId
								includeNavBoostScore: $includeNavBoostScore
								includeDeactivatedContributors: $includeDeactivatedContributors
								includeGithubCode: $includeGithubCode
								includeRisk: $includeRisk
								includeReactionsAndCommentCount: $includeReactionsAndCommentCount
							)
						edges {
							node {
								__typename
							}
						}
						pageInfo {
							endCursor
							hasNextPage
						}
					}
				}
			}
		`,
		queryData,
	);

	const cursor = data.search?.search?.pageInfo?.endCursor;
	const fetchNextPage = useCallback(
		(nextCursor: string) => {
			if (nextCursor === cursor) {
				loadNext(PAGINATION_UNUSED_PAGE_SIZE);
				hasPreviousRef.current = true;
			}
		},
		[loadNext, cursor],
	);

	return (
		<SearchResultsBody
			connectionData={data.search?.search ?? null}
			isCachedResult={data.search?.search?.isCachedResult}
			isLoadingNext={isLoadingNext}
			hasPreviousRef={hasPreviousRef}
			cursor={cursor}
			fetchNextPage={fetchNextPage}
			queryReference={queryReference}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsBody = ({
	connectionData,
	isCachedResult,
	isLoadingNext,
	hasPreviousRef,
	cursor,
	fetchNextPage,
	queryReference,
	sharedProps,
}: SearchResultsBodyProps): React.JSX.Element => {
	const {
		aggAbsoluteUrl,
		cloudId,
		thirdPartyConfigs,
		selectedProduct,
		primaryProduct,
		primarySubProduct,
		appMode,
		searchSessionId,
		searchQuery,
		availableProducts,
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		setResultsState,
		getSelectedFilters,
		clearFilters,
		logError,
		clearReferrer,
		referrer,
		autoCorrectPortalRef,
		smartAnswersPortalRef,
		debugPortalRef,
		setFederatedSlackCount,
		setFederatedOutlookMailCount,
		setFederatedGmailCount,
		setHasMoreGmailResults,
		setHasMoreOutlookMailResults,
		setProductsWithTimeout,
		bodyRef,
		maxResultsToShow,
		setMaxResultsToShow,
		mainContentWidth,
		backendExperimentRef,
		renderConnectorPromo,
		selectedSites,
	} = sharedProps;

	// Fragment for the search result body content within the connection
	const bodyData =
		useFragment<searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key>(
			graphql`
				fragment searchResults_AtlassianSearchPage_SearchResultsBody_connectionData on SearchItemConnection
				@argumentDefinitions(
					includeAssets: { type: "Boolean!", provider: "./is-assets-enabled.relayprovider" }
					includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
					includePassionfruitAsk: { type: "Boolean", defaultValue: false }
					includeRisk: { type: "Boolean", defaultValue: false }
					includePositions: { type: "Boolean", defaultValue: false }
					includeThirdPartyId: { type: "Boolean", defaultValue: false }
					includeNavBoostScore: { type: "Boolean", defaultValue: false }
					includeDeactivatedContributors: { type: "Boolean", defaultValue: false }
					includeGithubCode: { type: "Boolean", defaultValue: false }
					includeReactionsAndCommentCount: { type: "Boolean", defaultValue: false }
				) {
					edges {
						node {
							__typename
							id
							description
							linkedResults {
								id
								category
								connectorType
							}
							...searchResult_AtlassianSearchPage
								@arguments(
									includeAssets: $includeAssets
									includeJiraLiteModeFields: $includeJiraLiteModeFields
									includePassionfruitAsk: $includePassionfruitAsk
									includeRisk: $includeRisk
									includePositions: $includePositions
									includeThirdPartyId: $includeThirdPartyId
									includeNavBoostScore: $includeNavBoostScore
									includeDeactivatedContributors: $includeDeactivatedContributors
									includeGithubCode: $includeGithubCode
									includeReactionsAndCommentCount: $includeReactionsAndCommentCount
								)
							... on SearchResultGraphDocument {
								# required by analytics
								providerId
								integrationId
								connectorType
								connectorId
								connectorProduct
							}
							... on SearchResultCompassComponent {
								# we need this in this file so we can pass down the lazy-loaded team name
								ownerId
							}
							... on SearchResultJiraIssue {
								# we need this in this file so we can pass down the lazy-loaded issue status
								url
								issue {
									webUrl
								}
							}
							... on SearchConfluencePageBlogAttachment {
								# required by analytics
								socialSignal(tenantId: $tenantId) {
									signal
								}
							}
						}
					}
					queryInfo {
						originalQuery
						...autoCorrection_AtlassianSearchPage
					}
					pageInfo {
						endCursor
						hasNextPage
					}
					traceId
					errors {
						statusCode
						product
						integration
					}
				}
			`,
			connectionData,
		);

	// It is possible that aggregator returns the same node twice, but then <SearchResult key={edge.node.id} /> breaks
	// So we need to dedup by id
	const edges = useMemo(() => {
		const seenIds = new Set();
		const edges = bodyData?.edges || [];
		return edges.filter((edge) => {
			const id = edge?.node?.id;
			if (seenIds.has(id)) {
				return false;
			} else {
				seenIds.add(id);
				return true;
			}
		});
	}, [bodyData?.edges]);

	const edgesToDisplay = useRenderResultsOptimisation({
		bodyRef,
		edges,
		maxResultsToShow,
		setMaxResultsToShow,
		queryReference,
	});

	const [features] = useFeatures();
	const isLiteMode = features?.lite?.enabled ?? false;

	const productsWithTimeoutsFromErrors: ProductKeys[] | undefined = useMemo(() => {
		const errors = bodyData?.errors || [];

		return errors
			.filter((e: { statusCode: number }) => e.statusCode === 408)
			.map((e) => e.integration ?? e.product)
			.flatMap((s) => (s && isProductKey(s) ? [s] : [])); // Flatmap for better type inference
	}, [bodyData?.errors]);

	useEffect(() => {
		// We only want to set products with timeout in all product search
		if (selectedProduct !== undefined) {
			return;
		}

		setProductsWithTimeout?.(productsWithTimeoutsFromErrors);
	}, [productsWithTimeoutsFromErrors, selectedProduct, setProductsWithTimeout]);

	const resultsForAnalytics = useMemo(() => transformResultsForAnalytics(edges), [edges]);
	const resultsLength = edges.length;
	const rawResultsLength = bodyData?.edges?.length ?? 0;

	// Set federated Slack count when selectedProduct is Slack
	useEffect(() => {
		const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
			selectedProduct,
			undefined,
			{ fireExperimentExposure: false },
			thirdPartyConfigs,
		);
		if (
			selectedProduct === ProductKeys.Slack &&
			setFederatedSlackCount &&
			isSlackFederatedSearchEnabled
		) {
			setFederatedSlackCount(resultsLength);
		}
	}, [selectedProduct, resultsLength, setFederatedSlackCount, thirdPartyConfigs]);

	// Preserve existing Outlook Mail exact-count behaviour while the incremental count feature is off.
	useEffect(() => {
		if (
			selectedProduct === ProductKeys3P.OutlookMail &&
			setFederatedOutlookMailCount &&
			!fg('formula_3p_outlook_result_count')
		) {
			setFederatedOutlookMailCount(resultsLength);
			setHasMoreOutlookMailResults?.(false);
		}
	}, [selectedProduct, resultsLength, setFederatedOutlookMailCount, setHasMoreOutlookMailResults]);

	useFederatedEmailResultCount({
		selectedProduct,
		rawResultsLength,
		resultsLength,
		pageInfo: bodyData?.pageInfo,
		isLoadingNext,
		setFederatedGmailCount,
		setHasMoreGmailResults,
		setFederatedOutlookMailCount,
		setHasMoreOutlookMailResults,
	});

	const is3pGroupingEnabled = fg('fps_3p_grouping_rollout_gate');

	// Records dwell time for search results
	// TODO: refactor this to use props instead of RSS
	useDwellTime();

	// Compass team names
	const [teamNames, setTeamNames] = useState<Record<string, string | null>>({});

	const { teamIds, nodeIdMap } = useMemo(
		() => getCompassTeamIds(edges, selectedProduct),
		[edges, selectedProduct],
	);

	useEffect(() => {
		fetchAndUpdateTeamNames(cloudId, teamIds, nodeIdMap, setTeamNames, logError);
	}, [cloudId, teamIds, nodeIdMap, logError]);

	// Jira issue statuses
	const [issueStatuses, setIssueStatuses] = useState<
		Record<string, JiraIssueStatusAndColorName | null>
	>({});

	const { issueKeys, issueKeysByCloudId } = useMemo(
		() => getJiraIssueHydrationInputs(getJiraIssueHydrationItems(edges)),
		[edges],
	);

	useEffect(() => {
		fetchAndUpdateJiraFields(
			cloudId,
			issueKeys,
			aggAbsoluteUrl,
			setIssueStatuses,
			logError,
			undefined,
			primaryProduct,
			issueKeysByCloudId,
		);
	}, [
		cloudId,
		issueKeys,
		aggAbsoluteUrl,
		setIssueStatuses,
		logError,
		primaryProduct,
		issueKeysByCloudId,
	]);

	// MS Teams past meeting occurrences hydration
	// Fetches up to 5 past occurrences of a recurring meeting via TWG Cypher query.
	// Only runs for MS Teams integration results (identified by integrationId).
	const [pastMeetingOccurrences, setPastMeetingOccurrences] = useState<PastMeetingOccurrencesMap>(
		{},
	);
	const msTeamsVideoAris = useMemo(() => getMsTeamsVideoAris(edges), [edges]);
	const fetchedMsTeamsArisRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const newAris = msTeamsVideoAris.filter((ari) => !fetchedMsTeamsArisRef.current.has(ari));
		if (newAris.length === 0) {
			return;
		}
		for (const ari of newAris) {
			fetchedMsTeamsArisRef.current.add(ari);
		}
		fetchAndUpdatePastMeetingOccurrences(
			newAris,
			cloudId,
			aggAbsoluteUrl,
			setPastMeetingOccurrences,
			logError,
		);
	}, [msTeamsVideoAris, cloudId, aggAbsoluteUrl, logError]);

	// Jira issue linked PRs (GitHub PR hydration).
	// Only enabled when the user has Jira available and the GitHub connector
	// authenticated. Hydration is lazy: getJiraIssueARIs returns [] when no
	// Jira issues are present in the result set, in which case the fetch
	// effect below short-circuits and no network request is made.
	const isJiraLinkedPRsEnabled = shouldHydrateJiraGitHubPRs(thirdPartyConfigs, availableProducts);
	const [linkedPRs, setLinkedPRs] = useState<IssueLinkedPRs>({});
	const { fireAnalyticsEvent: fireLinkedPRsAnalyticsEvent } = useAnalytics();

	const allIssueAris = useMemo(
		() => (isJiraLinkedPRsEnabled ? getJiraIssueARIs(edges) : []),
		[edges, isJiraLinkedPRsEnabled],
	);

	// Track which ARIs we've already requested to avoid duplicate fetches on pagination.
	// When edges grow (e.g., user loads more results), we only fetch PRs for newly added issues.
	// Uses a ref rather than state to avoid triggering re-renders or effect cycles.
	const fetchedArisRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!isJiraLinkedPRsEnabled) {
			return;
		}
		const newAris = allIssueAris.filter((ari) => !fetchedArisRef.current.has(ari));
		if (newAris.length === 0) {
			return;
		}
		for (const ari of newAris) {
			fetchedArisRef.current.add(ari);
		}
		fetchAndUpdateJiraLinkedPRs(
			newAris,
			cloudId,
			aggAbsoluteUrl,
			setLinkedPRs,
			logError,
			fireLinkedPRsAnalyticsEvent,
		);
	}, [
		allIssueAris,
		cloudId,
		aggAbsoluteUrl,
		logError,
		isJiraLinkedPRsEnabled,
		fireLinkedPRsAnalyticsEvent,
	]);

	const are3pResultsShown = useMemo(
		() => are3pItemsInResults(edges, thirdPartyConfigs),
		[edges, thirdPartyConfigs],
	);
	const resultsShown = useMemo(
		() => getResultsShown(edges, thirdPartyConfigs),
		[edges, thirdPartyConfigs],
	);

	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const wasContentClearedBySpaceChange = useWasContentClearedBySpaceChange();
	const searchActions = useSearchActions();

	const clearSpaceFilter = useCallback(async () => {
		await searchActions.clearSelectFilterOptions({ filterId: spaceFilter.id });
	}, [searchActions]);

	const intl = useIntl();
	const intentDetectionTriggered = useMemo(
		() => isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale),
		[primaryProduct, intl.locale],
	);

	useSearchResultsAnalytics({
		are3pResultsShown,
		backendExperimentRef,
		clearReferrer,
		cloudId,
		commonAttributes,
		edges,
		experimentConfig,
		getSelectedFilters,
		intentDetectionTriggered,
		isJiraIssueIndexed,
		isLoadingNext,
		isPaginatedQuery: hasPreviousRef.current,
		nonPrivacySafeAttributes,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		referrer,
		resultsForAnalytics,
		resultsShown,
		searchQuery,
		selectedSites,
	});

	useSearchResultsVitalStats({
		edges,
		searchSessionId,
		isCQL: false,
		isCachedResult: isCachedResult ?? undefined,
	});

	// Update if there are no results
	useEffect(() => {
		setResultsState(edges.length === 0 ? ResultsStates.NO_RESULTS : ResultsStates.RESULTS);
	}, [edges, setResultsState]);

	useAfterQueryPerformanceTiming(edges);

	const queryInfo = bodyData?.queryInfo;
	const containsSearchResults = edges.length > 0;

	const debugResults: DebugResult[] = debugPortalRef?.current
		? edges.map((edge) => edge?.node ?? {})
		: [];
	const traceId = bodyData?.traceId ?? undefined;

	const body = (
		<>
			<UFOCustomCohortData dataKey="wordCount" value={commonAttributes.wordCount} />
			{isCachedResult !== undefined && (
				<UFOCustomCohortData dataKey="asyncQueryCachedResult" value={!!isCachedResult} />
			)}
			{queryInfo &&
				autoCorrectPortalRef &&
				autoCorrectPortalRef.current &&
				createPortal(
					<AutoCorrection node={queryInfo} appMode={appMode} cloudId={cloudId} />,
					autoCorrectPortalRef.current,
					'search-results-auto-correction',
				)}
			{debugPortalRef?.current &&
				createPortal(
					<DebugOverridesPanel
						traceId={traceId}
						backendExperiment={backendExperimentRef.current ?? undefined}
						results={debugResults}
					/>,
					debugPortalRef.current,
				)}
			{
				// the new Rovo Home experience renders Jira issues and projects in a table view in FPS
				isLiteMode ? (
					<SearchResultsListLiteRenderer
						containsSearchResults={containsSearchResults}
						edgesToDisplay={edgesToDisplay}
						renderConnectorPromo={renderConnectorPromo}
						cloudId={cloudId}
						appMode={appMode}
						searchQuery={searchQuery}
						thirdPartyConfigs={thirdPartyConfigs}
						selectedSites={selectedSites}
						selectedProduct={selectedProduct}
						primaryProduct={primaryProduct}
						primarySubProduct={primarySubProduct}
						searchSessionId={searchSessionId}
						originalQuery={queryInfo?.originalQuery}
						availableProducts={availableProducts}
						logError={logError}
						teamNames={teamNames}
						issueStatuses={issueStatuses}
						linkedPRs={linkedPRs}
						pastMeetingOccurrences={pastMeetingOccurrences}
						is3pGroupingEnabled={is3pGroupingEnabled}
						smartAnswersPortalRef={smartAnswersPortalRef}
						mainContentWidth={mainContentWidth}
						wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
						clearSpaceFilter={clearSpaceFilter}
						clearFilters={clearFilters}
						getSelectedFilters={getSelectedFilters}
						cursor={cursor}
						isLoadingNext={isLoadingNext}
						fetchNextPage={fetchNextPage}
					/>
				) : (
					<SearchResultsListDefaultRenderer
						containsSearchResults={containsSearchResults}
						edgesToDisplay={edgesToDisplay}
						renderConnectorPromo={renderConnectorPromo}
						cloudId={cloudId}
						appMode={appMode}
						searchQuery={searchQuery}
						thirdPartyConfigs={thirdPartyConfigs}
						selectedSites={selectedSites}
						selectedProduct={selectedProduct}
						primaryProduct={primaryProduct}
						primarySubProduct={primarySubProduct}
						searchSessionId={searchSessionId}
						originalQuery={queryInfo?.originalQuery}
						availableProducts={availableProducts}
						logError={logError}
						teamNames={teamNames}
						issueStatuses={issueStatuses}
						linkedPRs={linkedPRs}
						pastMeetingOccurrences={pastMeetingOccurrences}
						is3pGroupingEnabled={is3pGroupingEnabled}
						smartAnswersPortalRef={smartAnswersPortalRef}
						mainContentWidth={mainContentWidth}
						wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
						clearSpaceFilter={clearSpaceFilter}
						clearFilters={clearFilters}
						getSelectedFilters={getSelectedFilters}
						cursor={cursor}
						isLoadingNext={isLoadingNext}
						fetchNextPage={fetchNextPage}
					/>
				)
			}
		</>
	);

	return body;
};

export const are3pItemsInResults: (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => boolean = (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	return !!edges?.some(({ node }) => {
		const productKey =
			getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
			getIntegrationKey(node?.integrationId);
		if (!productKey) {
			// if no integration id it's a 1p
			return false;
		}
		return is3pProductKeyForAnalytics(productKey);
	});
};

type MinimalCompassComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		ownerId?: string | null;
	} | null;
};

function getCompassTeamIds(
	edges: readonly MinimalCompassComponentEdge[],
	selectedProduct?: ProductKeys,
): { teamIds: string[]; nodeIdMap: Record<string, string | null> } {
	if (selectedProduct !== ProductKeys.Compass && selectedProduct !== undefined) {
		return { teamIds: [], nodeIdMap: {} };
	}
	const nodeIdMap: Record<string, string | null> = {};
	const ids = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		let ownerId = node?.ownerId;
		if (isSearchCompassComponent(node)) {
			const teamId = getTeamId(ownerId);
			if (teamId) {
				nodeIdMap[node.id] = teamId;
				ids.add(teamId);
			} else {
				nodeIdMap[node.id] = null;
			}
		}
	}
	return { teamIds: Array.from(ids), nodeIdMap };
}

type MinimalJiraIssueComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		url?: string | null;
		issue?: {
			webUrl?: string | null;
		} | null;
	} | null;
};

type MinimalMsTeamsVideoEdge =
	| {
			node:
				| {
						__typename: string;
						id: string;
						integrationId?: string | null;
				  }
				| null
				| undefined;
	  }
	| null
	| undefined;

/**
 * Extracts ARIs for MS Teams meeting transcript results from the result edges.
 * These are SearchResultGraphDocument nodes with the Microsoft integration ID whose
 * entity ARI resource type is 'video' (e.g. ari:cloud:graph::video/teams/...).
 *
 * We narrow by ARI resource type rather than `subtype` because `subtype` is null
 * in production for MS Teams meeting transcripts.
 */
function getMsTeamsVideoAris(edges: readonly MinimalMsTeamsVideoEdge[]): string[] {
	const arisSet = new Set<string>();
	for (const edge of edges) {
		const node = edge?.node;
		if (
			node?.id &&
			node.__typename === 'SearchResultGraphDocument' &&
			node.integrationId === 'ari:cloud:platform::integration/microsoft' &&
			node.id.includes('::video/')
		) {
			arisSet.add(node.id);
		}
	}
	return Array.from(arisSet);
}

function getJiraIssueARIs(edges: readonly MinimalJiraIssueComponentEdge[]): string[] {
	const issueArisSet = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		if (node?.id && isSearchJiraIssue(node)) {
			issueArisSet.add(node.id);
		}
	}
	return Array.from(issueArisSet);
}

function getJiraIssueHydrationItems(
	edges: readonly MinimalJiraIssueComponentEdge[],
): { id: string; issueKey: string }[] {
	const items: { id: string; issueKey: string }[] = [];
	for (const edge of edges) {
		const node = edge.node;
		if (node?.id && isSearchJiraIssue(node)) {
			const issueKey =
				node.issue?.webUrl?.split('/').slice(-1)[0] || node.url?.split('/').slice(-1)[0];
			if (issueKey) {
				items.push({ id: node.id, issueKey });
			}
		}
	}
	return items;
}

type Minimal3pItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
	} | null;
};

type MinimalLinkedResultItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
		description: string | null;
		linkedResults?: readonly { id: string; category: string }[] | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
	} | null;
};

export const getResultsShown: (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ResultShown[] = (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): ResultShown[] => {
	return (
		edges?.map(({ node }, index) => {
			const isCodeResult =
				node?.__typename === 'SearchResultGraphCode' && fg('rovo_github_code_search');

			const productKey = isCodeResult
				? ProductKeys.Github
				: getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
					getIntegrationKey(node?.integrationId);

			// TODO(QS-10212): GitHub code results currently have git SHAs (not ARIs) and no connector
			// fields in the aggregator payload, so skip ARI-based product detection and short-circuit to
			// GitHub. Revisit when the aggregator schema gap is closed and other 3P code providers
			// (GitLab, Bitbucket, etc.) are added.
			const [firstPartyProduct] = isCodeResult
				? [undefined]
				: getProductAndIDFromARI(node?.id || '');

			let hasLinkInDescription = false;
			if (!isCodeResult) {
				const resultAri = AnyAri.parse(node?.id || '');
				if (resultAri.resourceOwner in products1P) {
					const links = getLinksFromText(node?.description ?? '');
					if (links && links.length > 0) {
						hasLinkInDescription = true;
					}
				}
			}

			const shouldShowBoostedLinks = node?.linkedResults
				? node?.linkedResults.length > 0 && index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
				: undefined;

			const connectorSourceType = getResultConnectorSource({
				connectorType: node?.connectorType,
				resultNodeId: node?.id || '',
				productKey: productKey,
				thirdPartyConfigs,
			});

			// Excludes code results (git SHAs, not ARIs - see PR description).
			const connectorInfo =
				productKey && !isCodeResult
					? {
							provider_id: node?.providerId || undefined,
							connector_id: node?.connectorId || undefined,
							connector_position: index,
							connector_product: node?.connectorProduct || undefined,
							connector_type: node?.connectorType || undefined,
							ari: node?.id,
							metadata: {
								// Add connectorWorkspaceId when available from backend
								// For now, only include fanoutResultsShown if present
								...(node?.linkedResults &&
								node?.linkedResults.length > 0 &&
								index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
									? {
											fanOutResultsShown: node.linkedResults
												.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE)
												.map((link) => {
													let is3P = false;
													try {
														is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
													} catch {
														is3P = false;
													}
													return {
														id: link.id,
														category: link.category,
														is3P,
													};
												}),
										}
									: {}),
							},
						}
					: undefined;

			return {
				id: node?.id,
				position: index,
				product: productKey || firstPartyProduct,
				metadata: {
					connectorType: connectorSourceType,
					boostedLinksShown: shouldShowBoostedLinks,
					fanoutResultsShown:
						node?.linkedResults &&
						node?.linkedResults.length > 0 &&
						index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
							? node.linkedResults.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE).map((link) => {
									let is3P = false;
									try {
										is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
									} catch {
										is3P = false;
									}
									return {
										id: link.id,
										category: link.category,
										is3P,
									};
								})
							: undefined,
					hasLinkInDescription,
				},
				connectorInfo,
			} as ResultShown;
		}) || []
	);
};

type MinimalAnalyticsEdge = {
	node?: {
		id: string;
		__typename?: string;
		integrationId?: string | null;
		providerId?: string | null;
		socialSignal?: any | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
	} | null;
};

/**
 * Relay and RSS results have different types
 * Shared code (like result analytics) has typing assuming RSS
 * We solve this by building interfaces that accept both, such as IAnalyticsSearchPageResults
 *
 * We likely need a better approach for coalescing RSS and Relay result node typing, for shared code as we migrate away from RSS
 */
const transformResultsForAnalytics = (
	edges: MinimalAnalyticsEdge[],
): IAnalyticsSearchPageResults => {
	const nodes =
		edges
			?.filter((edge) => !!edge?.node) // drop undefined nodes
			?.map((edge) => {
				const node = edge.node;
				return {
					id: node?.id || '',
					__typename: node?.__typename || '',
					integrationId: node?.integrationId || '',
					providerId: node?.providerId || '',
					socialSignal: node?.socialSignal?.signal ? node?.socialSignal : null,
					connectorType: node?.connectorType || null,
				};
			}) || [];

	return {
		edges: nodes.map((node) => {
			return { node };
		}),
	};
};
